import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPremiumResellerPackagesV1PackagesUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/resellers/v1/packages',
        destPath: '/v1/packages',
      },
      {
        srcPath: '/resellers/v2/packages',
        destPath: '/v2/packages',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_packages_packages';

/**
 * Creates a new package.
 *
 * Deprecated: This method is deprecated and will be removed by 2024-12-31.
 * Use com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2 instead.
 *
 * HTTP Request:
 * POST /v1/packages
 *
 * Permissions:
 * Requires the "premium.reseller-package.manage" permission with MANUAL type.
 *
 * Event:
 * A PRIVATE callback will be triggered when the package is successfully created.
 *
 * Required Fields:
 * - CreatePackageRequest.products
 *
 * Maturity: GA
 * Exposure: PUBLIC
 * @deprecated It has been replaced with com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2(), and will be removed on 2024-12-31.
 */
export function createPackage(payload: object): RequestOptionsFactory<any> {
  function __createPackage({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.createdDate' },
          { path: 'products.updatedDate' },
          { path: 'products.expirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.CreatePackage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPackage;
}

/**
 * Creates a package of product instances.
 *
 *
 * You must pass the relevant Wix account ID in the header of the call. In
 * the DIY flow, we recommend to pass the customer's sub-account ID instead
 * of your main reseller account ID.
 *
 * You may also pass a Wix site ID for each product in the body of the call.
 * If you omit the site ID, a floating product instance is created.
 *
 * When Wix customers purchase a specific paid service or product, Wix may offer
 * them time-limited free access to a different product. For example, customers
 * get a voucher for a free 1-year domain registration when purchasing any Wix
 * Premium plan. If you want to offer your customers the same benefit, create a
 * package containing the original product first. Then, create a second package
 * with the additional product. In the second Create Package call, pass the
 * instance ID of the original product as `referenceProductInstanceId`. This way,
 * Wix doesn't charge you for the additional product. Make sure that the status
 * of the referenced product is either `"PENDING"` or `"ACTIVE"`. Note that you
 * can use each product instance only a single time as reference instance.
 *
 * You need to pass a `countryCode` to specify where customers can claim
 * vouchers that may come with a product. Contact the
 * [Wix B2B sales team](mailto:bizdev@wix.com) to get information about
 * vouchers and supported locations.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 */
export function createPackageV2(payload: object): RequestOptionsFactory<any> {
  function __createPackageV2({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.createdDate' },
          { path: 'products.updatedDate' },
          { path: 'products.expirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.CreatePackageV2',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v2/packages',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createPackageV2;
}

/**
 * Cancels all product instances included in the package and the customer
 * immediately loses access to the canceled functionality.
 *
 *
 * You must pass the ID of the Wix account that the package belongs to in the
 * header of the call. The call fails, if the package and Wix account don't
 * match.
 *
 * If a canceled product instance is a requirement for another Wix service, that
 * functionality is also no longer available to the customer. For example, if
 * you cancel a Premium plan, a previously connected domain is automatically
 * disconnected from the site.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 */
export function cancelPackage(payload: object): RequestOptionsFactory<any> {
  function __cancelPackage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.CancelPackage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelPackage;
}

/**
 * Retrieves a package.
 *
 *
 * You must pass the ID of the Wix account that the package belongs to in the
 * header of the call. The call fails, if the package and Wix account don't
 * match.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 */
export function getPackage(payload: object): RequestOptionsFactory<any> {
  function __getPackage({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.GetPackage',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getPackage;
}

/**
 * Retrieves a list of packages, given the provided paging, filtering, and sorting.
 *
 *
 * You must pass the ID of the Wix account that the packages belong to in the
 * header of the call. The call returns packages that belong to the parent
 * account, regardless if you pass the ID of a parent or sub-account. In case
 * you want to retrieve only packages that belong to a sub-account, you must
 * pass the sub-account ID as `filter` in the `query` object like this:
 *
 * ```json
 * {
 * "query": {
 * "filter": {
 * "accountId": {
 * "$eq": "<SUB_ACCOUNT_ID>"
 * }
 * }
 * }
 * }
 * ```
 *
 * Query Packages runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * By default `pagingMetadata.cursors` are returned, unless you specifically pass
 * `query.paging`.
 *
 * The maximum for `cursorPaging.limit` is `100`.
 *
 * If you pass a cursor token that you have received in a previous Query Package response,
 * passing additional sort or filter information results in an invalid cursor
 * error. Since the received cursor token already holds all the needed information
 * for sort and filter. Changing the sort and filter properties during the span of
 * a cursor query isn't supported. But you can provide a different limit.
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 */
export function queryPackages(payload: object): RequestOptionsFactory<any> {
  function __queryPackages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.QueryPackages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'packages.createdDate' },
              { path: 'packages.updatedDate' },
              { path: 'packages.productInstances.createdDate' },
              { path: 'packages.productInstances.updatedDate' },
              { path: 'packages.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryPackages;
}

/**
 * Assigns a product instance to a site.
 *
 *  You must pass the ID of the Wix account that the product instance belongs to in the header of the call.
 *
 *  The customer immediately gains access to features included in the assigned service. It's up to the reseller to decide whether assigning a product instance affects the customer’s payment.
 *
 *  It's possible to assign a product instance that's already assigned to a site, to a different site without [unassigning](/packages/unassign-product-instance-from-site) it first. But if the call fails in such a situation, the product instance may either remain assigned to the original site or become unassigned. You can confirm the instance's status by [retrieving the relevant package](/packages/get-package).
 *
 * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
 */
export function assignProductInstanceToSite(
  payload: object
): RequestOptionsFactory<any> {
  function __assignProductInstanceToSite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.AssignProductInstanceToSite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/product-instances/{instanceId}/{siteId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __assignProductInstanceToSite;
}

/**
 * Unassigns a product instance from a site.
 *
 *  You must pass the ID of the Wix account that the product instance belongs to in the header of the call.
 *
 *  The product instance becomes floating and the customer immediately looses access to features included in the unassigned service. It's up to the reseller to decide whether unassigning a product instance affects the customer’s payment.
 *
 *  If an unassigned product instance is a requirement for another service, that service may not be available to the customer any longer. For example, if you unassign a `Combo Plan` plan from a site, a previously connected domain is disconnected.
 *
 *  You can assign floating product instances to sites using the [Assign Product Instance to Site](/packages/assign-product-instance-to-site) method.
 *
 * > **Important:** This call requires an account level API key and cannot be authenticated with the standard authorization header.
 */
export function unassignProductInstanceFromSite(
  payload: object
): RequestOptionsFactory<any> {
  function __unassignProductInstanceFromSite({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.UnassignProductInstanceFromSite',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/product-instances/{instanceId}/unassign',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __unassignProductInstanceFromSite;
}

/**
 * Cancels a product instance.
 *
 *
 * You must pass the ID of the Wix account that the product instance belongs
 * to in the header of the call. The call fails, if the product instance and
 * Wix account don't match.
 *
 * The customer immediately loses access to features included in the
 * canceled service. It's up to the reseller to decide whether the adjustment
 * affects the customer’s payment.
 *
 * If a canceled service is a requirement for another service, that service
 * may not be available to the customer any longer. For example, if you cancel
 * a `Combo Plan` plan, a previously connected domain is disconnected
 * from the site.
 *
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 */
export function cancelProductInstance(
  payload: object
): RequestOptionsFactory<any> {
  function __cancelProductInstance({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.CancelProductInstance',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/product-instances/{instanceId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __cancelProductInstance;
}

/**
 * Upgrades or downgrades a product instance. For example, you can upgrade a
 * customer's `Business Unlimited Premium` plan to `Business VIP` using this
 * endpoint.
 *
 *
 * You must pass the ID of the Wix account that the product instance belongs
 * to in the header of the call. The call fails, if the product instance and
 * Wix account don't match.
 *
 * The customer has immediate access to new features, while losing access to
 * features not included in the new service. It's up to the reseller to
 * decide whether the adjustment affects the customer’s payment.
 *
 * You can only exchange a product instance with a service of the same type.
 * Contact the [Wix B2B sales team](mailto:bizdev@wix.com) for details about
 * which services belong to the same type.
 *
 * You must provide either a new `catalogProductId` or a new `billingInfo`
 * object. The new billing cycle must be supported for the product. Contact the
 * [Wix B2B sales team](mailto:bizdev@wix.com) for information
 * about which billing cycles are supported for each product. If you adjust the
 * billing cycle for a product instance with `RECURRING` payments, you must
 * also provide `billingInfo.cycleDuration.unit`.
 *
 * If a removed feature is a requirement for another service, that service
 * may not be available to the customer any longer. For example, if you downgrade a
 * `Business Unlimited` plan to `Business Basic`, the site owners won’t be
 * able to use `Pro eCommerce Features` any longer.
 *
 * > **Important**: This call requires an account level API key and cannot be
 * > authenticated with the standard authorization header.
 */
export function adjustProductInstanceSpecifications(
  payload: object
): RequestOptionsFactory<any> {
  function __adjustProductInstanceSpecifications({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.AdjustProductInstanceSpecifications',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/product-instances/{instanceId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'package.createdDate' },
              { path: 'package.updatedDate' },
              { path: 'package.productInstances.createdDate' },
              { path: 'package.productInstances.updatedDate' },
              { path: 'package.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __adjustProductInstanceSpecifications;
}

/** Updates a package's external ID field. */
export function updatePackageExternalId(
  payload: object
): RequestOptionsFactory<any> {
  function __updatePackageExternalId({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.premium.reseller.v1.packages',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.premium.reseller.packages.v1.Packages.UpdatePackageExternalId',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPremiumResellerPackagesV1PackagesUrl({
        protoPath: '/v1/packages/update/{packageId}/{externalId}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'updatedPackage.createdDate' },
              { path: 'updatedPackage.updatedDate' },
              { path: 'updatedPackage.productInstances.createdDate' },
              { path: 'updatedPackage.productInstances.updatedDate' },
              { path: 'updatedPackage.productInstances.expirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updatePackageExternalId;
}
